import React, { useState, useRef, useEffect } from "react";
import {
  usePhoneInput,
  defaultCountries,
  parseCountry,
  FlagImage,
} from "react-international-phone";
import { ChevronDown, CheckCircle2, XCircle } from "lucide-react";
import "react-international-phone/style.css";
import "./style.css";
import { getCountryName } from "../../utils/countryNamesPtBR";
import {
  filterCountries,
  isValidContactPhone,
  toStoredPhoneDigits,
} from "../../helpers/contactPhoneValidation";

const LoginPhoneInput = ({
  value = "",
  onChange,
  placeholder = "",
  required = false,
  autoComplete = "off",
  ...rest
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const menuRef = useRef(null);

  const phoneInput = usePhoneInput({
    defaultCountry: "br",
    value,
    countries: defaultCountries,
    onChange: (data) => {
      const sanitized = toStoredPhoneDigits(data.phone);
      const dial = data.country?.dialCode || "";
      const national =
        dial && sanitized.startsWith(dial)
          ? sanitized.slice(dial.length)
          : sanitized;
      onChange?.(national ? sanitized : "");
    },
  });

  const countryList = defaultCountries.map(parseCountry);
  const filteredCountries = filterCountries(countryList, searchTerm);
  const isValid = isValidContactPhone(phoneInput.phone);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        setSearchTerm("");
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const handleMenuKeyDown = (e) => {
    if (/[a-zà-ú]/i.test(e.key) && e.key.length === 1) {
      e.preventDefault();

      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const newSearchTerm = searchTerm + e.key;
      setSearchTerm(newSearchTerm);

      const timeout = setTimeout(() => {
        setSearchTerm("");
      }, 1000);

      setSearchTimeout(timeout);
    }
  };

  const handleCountrySelect = (country) => {
    phoneInput.setCountry(country.iso2);
    setMenuOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="login-phone-input-container" ref={menuRef}>
      <div className="login-input-wrapper">
        <button
          type="button"
          className="login-phone-country-selector"
          aria-label="Selecionar país"
          onClick={() => setMenuOpen(!menuOpen)}
          onKeyDown={handleMenuKeyDown}
          tabIndex={0}
        >
          <FlagImage iso2={phoneInput.country.iso2} size="20px" />
          <ChevronDown size={14} className="login-phone-country-arrow" />
        </button>

        <input
          className="login-input login-phone-input-field"
          type="tel"
          inputMode="tel"
          placeholder={placeholder}
          value={phoneInput.inputValue || phoneInput.phone}
          onChange={phoneInput.handlePhoneValueChange}
          required={required}
          autoComplete={autoComplete}
          aria-invalid={phoneInput.phone ? !isValid : undefined}
          {...rest}
        />
        {toStoredPhoneDigits(phoneInput.phone).length > 6 && (
          <span className="login-phone-validity" aria-hidden>
            {isValid ? (
              <CheckCircle2 size={18} color="#10b981" />
            ) : (
              <XCircle size={18} color="#ef4444" />
            )}
          </span>
        )}
      </div>

      {menuOpen && (
        <div className="login-phone-country-menu">
          <div className="login-phone-country-search">
            <input
              type="text"
              className="login-input"
              placeholder="Buscar país, DDI ou código"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          <div className="login-phone-country-list">
            {filteredCountries.map((country) => (
              <button
                key={country.iso2}
                type="button"
                className="login-phone-country-item"
                onClick={() => handleCountrySelect(country)}
              >
                <FlagImage iso2={country.iso2} size="20px" />
                <span className="login-phone-country-name">
                  {getCountryName(country.iso2)}
                </span>
                <span className="login-phone-country-dial">
                  +{country.dialCode}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPhoneInput;
